/**
 * The dungeon level format: what the admin editor writes and what the game
 * client and battle server read. Plain JSON only -- no engine or network types
 * leak in here, so a level can be stored, diffed and shared as-is.
 */

/** Bumped whenever a stored level can no longer be read as-is. */
export const DUNGEON_LEVEL_FORMAT_VERSION = 1;

/**
 * World units (metres) per grid cell. Dungeons are authored on a grid, not free-form.
 *
 * 5 m matches the Synty modular art: every pack's floor tiles measure 5 m, with some
 * 2.5 m half-modules. See develop/ExploreDungeonAssets.md §5.3.
 *
 * Note the art is authored corner-origin (a tile spans 0..5, not -2.5..+2.5) while
 * `tileToWorld` below returns cell centres. That is correct for the placeholder boxes
 * and will need reconciling when the real meshes load.
 */
export const TILE_SIZE = 5;

export const TILE_KINDS = ['floor', 'wall', 'door', 'spawn', 'exit', 'trap', 'chest'] as const;
export type TileKind = (typeof TILE_KINDS)[number];

/** Tiles snap to 90-degree rotations so modular art pack pieces line up. */
export type TileRotation = 0 | 90 | 180 | 270;

export interface DungeonTile {
  /** Grid column. */
  x: number;
  /** Grid row. */
  z: number;
  kind: TileKind;
  rotation: TileRotation;
  /** Art pack prefab id; unset means "use the default prop for this kind". */
  prefab?: string;
}

export interface DungeonLevel {
  formatVersion: number;
  id: string;
  name: string;
  /** Player id of the builder who authored the level. */
  authorId: string;
  /** Grid extents, in cells. */
  width: number;
  height: number;
  tiles: DungeonTile[];
  /** ISO-8601. */
  createdAt: string;
}

export function createEmptyLevel(overrides: Partial<DungeonLevel> = {}): DungeonLevel {
  return {
    formatVersion: DUNGEON_LEVEL_FORMAT_VERSION,
    id: `lvl_${Math.random().toString(36).slice(2, 10)}`,
    name: 'Untitled Dungeon',
    authorId: 'anonymous',
    width: 16,
    height: 16,
    tiles: [],
    createdAt: new Date().toISOString(),
    ...overrides,
  };
}

/** Grid cell -> world-space centre of that cell. */
export function tileToWorld(x: number, z: number): { x: number; z: number } {
  return { x: x * TILE_SIZE, z: z * TILE_SIZE };
}

/** World-space position -> the grid cell containing it. */
export function worldToTile(x: number, z: number): { x: number; z: number } {
  return { x: Math.round(x / TILE_SIZE), z: Math.round(z / TILE_SIZE) };
}
